export default class WatchableObject<T extends Record<string, unknown>> {
  private object: T;
  listeners: Partial<{ [K in keyof T]: Array<(value: T[K]) => void> }> = {};

  constructor(initialObject: T) {
    this.object = initialObject;
    this.listeners = {};
  }

  setValue<K extends keyof T>(key: K, value: T[K]) {
    this.object[key] = value;
    if (!this.listeners[key]) {
      this.listeners[key] = [];
    }
    this.listeners[key]?.forEach((listener) => listener(value));
  }

  addListener<K extends keyof T>(key: K, listener: (value: T[K]) => void) {
    if (!this.listeners[key]) {
      this.listeners[key] = [];
    }
    this.listeners[key]?.push(listener);
  }

  removeListener<K extends keyof T>(key: K, listener: (value: T[K]) => void) {
    if (!this.listeners[key]) {
      return;
    }
    const index = this.listeners[key]?.indexOf(listener) ?? -1;
    if (index > -1) {
      this.listeners[key]?.splice(index, 1);
    }
  }

  getValue<K extends keyof T>(key: K): T[K] {
    const value = this.object[key];
    if (value instanceof Map) {
      return new Map(value) as unknown as T[K];
    }
    if (value === undefined) {
      console.warn('The following key has an undefined value in WatchableObject:', key);
      return {} as T[K];
    }
    try {
      return JSON.parse(JSON.stringify(value)) as unknown as T[K];
    } catch (error) {
      console.error('Error parsing value in WatchableObject:', error);
      return {} as T[K];
    }
  }
}
